// Ledger tab. Four stacked sections (each conditionally rendered):
//
//   Expected       (encounter scope, only when no charge_lines yet)
//     ↳ derived from EligibilitySnapshot's patient responsibility breakdown.
//       Surfaces predicted copay/coinsurance/deductible before the first
//       charge_line lands. Hides as soon as Realized/Receipt has data.
//
//   Receipt        (encounter scope, when /preview returns lines)
//     ↳ per-line CPT view from POST /v2/provider/charge-lines/preview with
//       encounter_id. Shows description, applied_payment, patient_due, and
//       an "already invoiced" warning. The authoritative "what to bill"
//       view — used as the receipt payers see and as the SettleSheet's
//       suggested_reason source.
//
//   Open invoices  (when any are open in the active scope)
//     ↳ statements / invoices the patient hasn't paid yet. Each row offers
//       an inline "Send reminder" link that bumps the BE's reminder count.
//
//   Activity       (always)
//     ↳ chronological ledger entries from /balance via PatientFinancialSummary.
//       Patient scope shows entries from all encounters with encounter labels.

import { formatDeliveryMethod } from '@features/billing/components/v2/shared';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useSendInvoiceReminder } from '@features/payments';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import type { ChargeLinePreviewResponse } from '@store/generated/billing-v2';

import type {
  EncounterLedgerGroup,
  LedgerEntryItem,
  PatientInvoiceHistoryItem,
} from '../types';

interface Props {
  entries: Array<LedgerEntryItem & { encounterLabel?: string | null }>;
  encounter?: EncounterLedgerGroup;
  preview?: ChargeLinePreviewResponse;
  openInvoices: PatientInvoiceHistoryItem[];
  isEncounterScope: boolean;
  /** Show encounter label on each row (patient scope only). */
  showEncounterLabels: boolean;
}

export const LedgerTabContent = ({
  entries,
  encounter,
  preview,
  openInvoices,
  isEncounterScope,
  showEncounterLabels,
}: Props) => {
  // Receipt = the authoritative per-line "what's owed" view from /preview.
  // Only shown in encounter scope when /preview returned lines.
  const receiptLines = isEncounterScope ? extractReceiptLines(preview) : [];
  const hasReceipt = receiptLines.length > 0;

  // Expected hides when Receipt has data (the realized lines supersede the
  // predicted breakdown). Also hidden in patient scope.
  const showExpected = isEncounterScope && !hasReceipt;
  const expected = showExpected ? deriveExpected(encounter) : [];

  return (
    <Box>
      {expected.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>Expected</SectionTitle>
          {expected.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                px: 1.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: ELIG_TOKENS.amberTint,
                border: `1px dashed ${ELIG_TOKENS.lineStrong}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="body2"
                  sx={{ color: ELIG_TOKENS.inkSoft, fontStyle: 'italic' }}
                >
                  {item.label}
                </Typography>
                <Tooltip title={item.rationale} arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: ELIG_TOKENS.inkFaint }} />
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
                {dollars(item.amount_cents)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {hasReceipt && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>Receipt</SectionTitle>
          {receiptLines.map((line) => (
            <ReceiptRow key={line.charge_line_id} line={line} />
          ))}
          {preview?.unallocated_credit_cents ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                pt: 1.5,
                mt: 0.5,
                borderTop: `1px dashed ${ELIG_TOKENS.line}`,
              }}
            >
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
                Applied credits
              </Typography>
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.green, fontWeight: 600 }}>
                −{dollars(preview.unallocated_credit_cents)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      )}

      {openInvoices.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>
            Open invoices ({openInvoices.length})
          </SectionTitle>
          {openInvoices.map((invoice) => (
            <OpenInvoiceRow key={invoice.invoice_id} invoice={invoice} />
          ))}
        </Box>
      )}

      <SectionTitle>{hasReceipt ? 'Activity' : 'Confirmed'}</SectionTitle>
      {entries.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: ELIG_TOKENS.inkFaint, fontStyle: 'italic', py: 1 }}
        >
          No charges or payments yet.
        </Typography>
      ) : (
        entries.map((entry) => (
          <ConfirmedRow key={entry.entry_id} entry={entry} showEncounter={showEncounterLabels} />
        ))
      )}
    </Box>
  );
};

// ─── Receipt row ─────────────────────────────────────────────────────────────

interface ReceiptLine {
  charge_line_id: string;
  cpt_code?: string | null;
  description?: string | null;
  patient_due_cents: number;
  applied_payment_cents: number;
  already_invoiced: boolean;
}

const extractReceiptLines = (
  preview: ChargeLinePreviewResponse | undefined,
): ReceiptLine[] => {
  if (!preview) return [];
  const lines: ReceiptLine[] = [];
  for (const enc of preview.encounters ?? []) {
    for (const line of enc.lines ?? []) {
      lines.push({
        charge_line_id: line.charge_line_id,
        cpt_code: line.cpt_code,
        description: line.description,
        patient_due_cents: line.patient_due_cents,
        applied_payment_cents: line.applied_payment_cents ?? 0,
        already_invoiced: line.already_invoiced ?? false,
      });
    }
  }
  return lines;
};

const ReceiptRow = ({ line }: { line: ReceiptLine }) => {
  const label =
    line.description ??
    (line.cpt_code ? `CPT ${line.cpt_code.replace(/^CPT:/, '')}` : 'Charge');
  const subtitle = line.cpt_code && line.description ? line.cpt_code.replace(/^CPT:/, 'CPT ') : null;
  const isPaid = line.patient_due_cents === 0;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
        {line.already_invoiced && (
          <Tooltip title="This line is already on an open invoice — sending another may double-bill the patient." arrow>
            <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: ELIG_TOKENS.amber, mt: 0.25 }} />
          </Tooltip>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
            {subtitle ?? ' '}
            {line.applied_payment_cents > 0 ? (
              <> · paid {dollars(line.applied_payment_cents)}</>
            ) : null}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: isPaid ? ELIG_TOKENS.green : ELIG_TOKENS.ink,
          fontWeight: 600,
          flexShrink: 0,
          ml: 2,
        }}
      >
        {isPaid ? 'Paid' : dollars(line.patient_due_cents)}
      </Typography>
    </Box>
  );
};

// ─── Open invoice row ────────────────────────────────────────────────────────

const OpenInvoiceRow = ({ invoice }: { invoice: PatientInvoiceHistoryItem }) => {
  const { sendReminder, isLoading } = useSendInvoiceReminder();
  const sentDate = new Date(invoice.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const handleRemind = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    void sendReminder(invoice.invoice_id);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600 }}>
          {dollars(invoice.amount_cents)}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          Sent {sentDate}
          {invoice.delivery_method ? ` via ${formatDeliveryMethod(invoice.delivery_method)}` : ''}
        </Typography>
      </Box>
      <Typography
        component="button"
        onClick={handleRemind}
        sx={{
          background: 'transparent',
          border: 0,
          cursor: isLoading ? 'default' : 'pointer',
          color: isLoading ? ELIG_TOKENS.inkFaint : ELIG_TOKENS.midnight,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'none',
          p: 0,
          '&:hover': isLoading ? {} : { textDecoration: 'underline' },
        }}
      >
        {isLoading ? 'Sending…' : 'Send reminder'}
      </Typography>
    </Box>
  );
};

// ─── Activity row (chronological) ────────────────────────────────────────────

const ConfirmedRow = ({
  entry,
  showEncounter,
}: {
  entry: LedgerEntryItem & { encounterLabel?: string | null };
  showEncounter: boolean;
}) => {
  const isCredit = entry.amount_cents < 0;
  const date = new Date(entry.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink }}>
          {entry.description ?? entry.entry_type_label}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {date}
          {showEncounter && entry.encounterLabel ? ` · ${entry.encounterLabel}` : ''}
          {' · '}
          {entry.entry_type_label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: isCredit ? ELIG_TOKENS.green : ELIG_TOKENS.ink,
          fontWeight: 600,
          flexShrink: 0,
          ml: 2,
        }}
      >
        {isCredit ? dollars(entry.amount_cents) : `+${dollars(entry.amount_cents)}`}
      </Typography>
    </Box>
  );
};

// ─── Expected derivation ─────────────────────────────────────────────────────

interface ExpectedItem {
  id: string;
  label: string;
  amount_cents: number;
  rationale: string;
}

const deriveExpected = (encounter: EncounterLedgerGroup | undefined): ExpectedItem[] => {
  if (!encounter) return [];
  const breakdown = encounter.patient_responsibility_breakdown;
  if (!breakdown) return [];
  const items: ExpectedItem[] = [];
  if (breakdown.copay_cents) {
    items.push({
      id: 'copay',
      label: 'Copay (predicted)',
      amount_cents: breakdown.copay_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  if (breakdown.coinsurance_cents) {
    items.push({
      id: 'coinsurance',
      label: 'Coinsurance (predicted)',
      amount_cents: breakdown.coinsurance_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  if (breakdown.deductible_cents) {
    items.push({
      id: 'deductible',
      label: 'Deductible (predicted)',
      amount_cents: breakdown.deductible_cents,
      rationale: 'From eligibility snapshot',
    });
  }
  return items;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      color: ELIG_TOKENS.midnight,
      fontWeight: 600,
      mb: 1,
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '−' : '';
  if (abs % 100 === 0) return `${sign}$${abs / 100}`;
  return `${sign}$${(abs / 100).toFixed(2)}`;
};
